import React, { type ReactElement } from 'react'
import type { SupplierTabProps } from '../hooks/useSupplierTabs'
import { useTranslation } from 'react-i18next'
import { type Control, useForm } from 'react-hook-form'
import LoadingSpinner from '../../common/components/LoadingSpinner'
import styles from '../assets/scss/Suppliers.module.scss'
import { Col, Form, Row } from 'react-bootstrap'
import FormControlTextArea from '../../common/components/FormControlTextArea'
import loaderStyles from '../../common/assets/scss/LoadingSpinner.module.scss'
import { handleAsync } from '../../common/utils/asyncHandler'
import MaNavBar from '../../common/components/MaNavBar'
import classNames from 'classnames'
import MaPanel from '../../common/components/MaPanel'
import { useFormDisabled } from '../../common/components/FormDisabledContext'

function CarriageCharges ({
  isLoading,
  hasLoadingError,
  supplierData,
  onSubmit,
  onDirtyChange
}: SupplierTabProps): ReactElement {
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
    reset
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: supplierData,
    defaultValues: supplierData
  })

  const isDisabled = useFormDisabled()

  React.useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  React.useEffect(() => {
    if (supplierData) {
      reset(supplierData)
    }
  }, [supplierData, reset])

  return (
    <div className={styles.supplierDataTab}>
      <MaNavBar
        onSubmit={handleAsync(handleSubmit(onSubmit))}
        formDirty={isDirty}
      />
      {(isLoading || hasLoadingError) && (
        <div className="flex items-center justify-center h-full">
          <div className={loaderStyles.spinner}>
            <LoadingSpinner />
          </div>
        </div>
      )}

      <MaPanel
        className={classNames(
          isDisabled ? styles['disabled-panel'] : '',
          'pt-2'
        )}
      >
        <MaPanel.Body>
          {!isLoading && (
            <>
              <div className={styles['section-title-panel']}>
                <h4 className={styles['section-title']}>
                  {t('suppliers.carriageCharges.tab.label.main')}
                </h4>
              </div>
              <Form onSubmit={handleAsync(handleSubmit(onSubmit))}>
                <Row>
                  <Col md={12}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.carriageCharges.tab.label.carriagePolicy'
                        )}
                      </Form.Label>
                      <FormControlTextArea
                        name={'carriagePolicy'}
                        maxLength={500}
                        disabled={isDisabled}
                        required={false}
                        control={control as unknown as Control}
                        value={watch('carriagePolicy') ?? ''}
                        rows={5}
                        aria-label={t(
                          'suppliers.carriageCharges.tab.label.carriagePolicy'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.carriagePolicy?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.carriageCharges.tab.label.standardCarriageCharge'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('standardCarriageCharge', {
                          disabled: isDisabled
                        })}
                        type="number"
                        isInvalid={!!errors.standardCarriageCharge}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.carriageCharges.tab.label.standardCarriageCharge'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.standardCarriageCharge?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.carriageCharges.tab.label.minimumOrderThreshold'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('minimumOrderThreshold', {
                          disabled: isDisabled
                        })}
                        isInvalid={!!errors.minimumOrderThreshold}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.carriageCharges.tab.label.minimumOrderThreshold'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.minimumOrderThreshold?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.carriageCharges.tab.label.minimumOrderCharge'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('minimumOrderCharge', {
                          disabled: isDisabled
                        })}
                        isInvalid={!!errors.minimumOrderCharge}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.carriageCharges.tab.label.minimumOrderCharge'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.minimumOrderCharge?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </MaPanel.Body>
      </MaPanel>
    </div>
  )
}
export default CarriageCharges
